import React from "react";
import {
  TextField,
  MenuItem,
  Box,
  Typography,
  Chip,
  SvgIcon,
  SvgIconProps,
} from "@mui/material";
import { Controller, Control } from "react-hook-form";

export const DropDownArrowIcon = (props: SvgIconProps) => (
  <SvgIcon {...props} viewBox="0 0 14 9">
    <path
      d="M6.31853 8.09591L0.747503 1.99634C0.670895 1.91213 0.610492 1.81809 0.566295 1.7142C0.522098 1.61064 0.5 1.4995 0.5 1.38077C0.5 1.14364 0.57322 0.93748 0.719659 0.762294C0.866098 0.587431 1.05909 0.5 1.29864 0.5H13.0347C13.2742 0.5 13.4672 0.588399 13.6137 0.765198C13.7601 0.941674 13.8333 1.14767 13.8333 1.38319C13.8333 1.44223 13.7507 1.64661 13.5854 1.99634L8.0148 8.09591C7.88693 8.23625 7.75463 8.33869 7.61792 8.40321C7.4812 8.46774 7.33078 8.5 7.16667 8.5C7.00255 8.5 6.85213 8.46774 6.71542 8.40321C6.5787 8.33869 6.4464 8.23625 6.31853 8.09591Z"
      fill="#989898"
    />
  </SvgIcon>
);

export interface SelectOption {
  value: string | number;
  label: string;
}

export interface CustomSelectProps {
  label?: string;
  name: string;
  control?: Control<any>;
  options: SelectOption[];
  required?: boolean;
  defaultValue?: string | number | (string | number)[];
  width?: string;
  XsWidth?: string;
  labelPosition?: "top" | "left";
  multiple?: boolean;
  placeholder?: string;
  renderValue?: (selected: any) => React.ReactNode;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  onChange?: (value: any) => void;
  /**
   * 選択中テキスト & ドロップダウン項目のフォントサイズ。
   * 既定 12px (フォーム用)。テーブルセル埋め込み時は "14px" を渡して
   * 周辺セルと文字サイズを揃える。
   */
  fontSize?: string;
  sx?: any;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  label,
  name,
  control,
  options,
  required = false,
  defaultValue,
  width = "124px",
  XsWidth = "90px",
  labelPosition = "left",
  multiple = false,
  placeholder,
  renderValue,
  disabled = false,
  error: externalError,
  helperText,
  onChange,
  fontSize,
  sx,
  ...props
}) => {
  // スタンドアロンモード用のstate
  const [value, setValue] = React.useState(
    multiple ? defaultValue || [] : defaultValue || ""
  );

  // デフォルトのrenderValue関数
  const defaultRenderValue = (selected: any) => {
    if (multiple) {
      if (!selected || (Array.isArray(selected) && selected.length === 0)) {
        return (
          <span style={{ color: "#989898" }}>
            {placeholder || "選択してください"}
          </span>
        );
      }

      if (Array.isArray(selected)) {
        return (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {selected.map((value) => {
              const option = options.find((opt) => opt.value === value);
              return (
                <Chip
                  key={value}
                  label={option?.label || value}
                  size="small"
                  sx={{
                    height: "20px",
                    fontSize: "10px",
                    "& .MuiChip-label": {
                      padding: "0 6px",
                    },
                  }}
                />
              );
            })}
          </Box>
        );
      }
    }
    // Single select mode
    if (!selected || selected === "") {
      return (
        <span style={{ color: "#989898" }}>
          {placeholder || "選択してください"}
        </span>
      );
    }

    const option = options.find((opt) => opt.value === selected);
    return option?.label || "";
  };

  // react-hook-formと統合されている場合
  if (control) {
    return (
      <Box
        // AI 画面コンテキスト用 (getPageContext が拾う)
        data-field={name || undefined}
        data-label={label || undefined}
        data-options={options && options.length > 0 ? JSON.stringify(options) : undefined}
        sx={
          labelPosition === "top"
            ? {
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 0.5,
              }
            : { display: "flex", alignItems: "center", gap: { lg: 2, xs: 1 } }
        }
      >
        {label && (
          <Typography
            variant="body2"
            sx={{
              fontSize: "12px",
              fontWeight: 700,
              color: "#3e3e3e",
              mb: labelPosition === "top" ? 0.5 : 0,
            }}
          >
            {label}
            {required && <span style={{ color: "#da384c" }}> *</span>}
          </Typography>
        )}
        <Controller
          name={name}
          control={control}
          defaultValue={multiple ? defaultValue || [] : defaultValue}
          render={({ field, fieldState: { error } }) => (
            <TextField
              select
              {...field}
              value={multiple ? field.value || [] : (field.value ?? "")}
              error={!!error || externalError}
              disabled={disabled}
              helperText={error?.message || helperText}
              onChange={(e) => {
                field.onChange(e);
                onChange?.(e.target.value);
              }}
              SelectProps={{
                multiple: multiple,
                renderValue: renderValue || defaultRenderValue,
                displayEmpty: true,
                IconComponent: DropDownArrowIcon,
                MenuProps: {
                  PaperProps: {
                    style: {
                      maxHeight: 224,
                    },
                  },
                },
              }}
              {...props}
              sx={{
                width: { lg: width, xs: XsWidth },
                ".MuiInputBase-root": {
                  height: multiple
                    ? { xs: "auto", sm: "auto" }
                    : { xs: "32px", sm: "34px" },
                  minHeight: { xs: "32px", sm: "34px" },
                  borderRadius: "8px",
                  fieldset: {
                    borderColor: error || externalError ? "#d32f2f" : "#D9D9D9",
                  },
                  "&:hover fieldset": {
                    borderColor: error || externalError ? "#d32f2f" : "#D9D9D9",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: error || externalError ? "#d32f2f" : "#D9D9D9",
                    borderWidth: "1px",
                  },
                },
                ".MuiSelect-select": {
                  padding: "8px 32px 8px 14px !important",
                  fontSize: fontSize ?? "12px",
                  display: multiple ? "flex" : "block",
                  alignItems: multiple ? "center" : "unset",
                  minHeight: multiple ? "18px" : "unset",
                },
                ".MuiSelect-icon": {
                  fontSize: "14px",
                  right: "12px !important",
                  marginRight: "0px !important",
                },
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "#D9D9D9",
                    borderWidth: "1px",
                  },
                },
                "& .MuiInputBase-input": {
                  color: "#3e3e3e",
                },
                ...sx,
              }}
            >
              {options
                .filter((option) => !multiple || option.value !== "")
                .map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{
                      fontSize: fontSize ?? { xs: "10px", sm: "12px" },
                      minHeight: { xs: "25px", sm: "30px" },
                      ...(multiple && {
                        "&.Mui-selected": {
                          backgroundColor: "rgba(11, 157, 189, 0.08)",
                        },
                        "&.Mui-selected:hover": {
                          backgroundColor: "rgba(11, 157, 189, 0.12)",
                        },
                      }),
                    }}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              {(!options || !Array.isArray(options) || options.length === 0) &&
                !placeholder && (
                  <MenuItem
                    value=""
                    disabled
                    sx={{
                      fontSize: { xs: "10px", sm: "12px" },
                      color: "#989898 !important",
                      padding: "8px 16px",
                      backgroundColor: "#EFF4FC",
                      "&.Mui-disabled": {
                        color: "#989898 !important",
                        backgroundColor: "#EFF4FC",
                        opacity: 1,
                      },
                    }}
                  >
                    選択してください
                  </MenuItem>
                )}
            </TextField>
          )}
          rules={{
            validate: (v) => {
              if (required) {
                if (multiple && (!v || v.length === 0)) return "必須項目です";
                if (!multiple && !v) return "必須項目です";
              }
              return true;
            },
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={
        labelPosition === "top"
          ? {
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 0.5,
            }
          : { display: "flex", alignItems: "center", gap: { lg: 2, xs: 1 } }
      }
    >
      {label && (
        <Typography
          variant="body2"
          sx={{
            fontSize: "12px",
            fontWeight: 700,
            color: "#3e3e3e",
            mb: labelPosition === "top" ? 0.5 : 0,
          }}
        >
          {label}
          {required && <span style={{ color: "#da384c" }}> *</span>}
        </Typography>
      )}
      <TextField
        select
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          onChange?.(e.target.value);
        }}
        error={externalError}
        disabled={disabled}
        helperText={helperText}
        SelectProps={{
          multiple: multiple,
          renderValue: renderValue || defaultRenderValue,
          displayEmpty: true,
          IconComponent: DropDownArrowIcon,
          MenuProps: {
            PaperProps: {
              style: {
                maxHeight: 224,
              },
            },
          },
        }}
        {...props}
        sx={{
          width: { lg: width, xs: XsWidth },
          ".MuiInputBase-root": {
            height: multiple
              ? { xs: "auto", sm: "auto" }
              : { xs: "32px", sm: "34px" },
            minHeight: { xs: "32px", sm: "34px" },
            borderRadius: "8px",
            fieldset: {
              borderColor: externalError ? "#d32f2f" : "#D9D9D9",
            },
            "&:hover fieldset": {
              borderColor: externalError ? "#d32f2f" : "#D9D9D9",
            },
            "&.Mui-focused fieldset": {
              borderColor: externalError ? "#d32f2f" : "#D9D9D9",
              borderWidth: "1px",
            },
          },
          ".MuiSelect-select": {
            padding: "8px 32px 8px 14px !important",
            fontSize: fontSize ?? "12px",
            display: multiple ? "flex" : "block",
            alignItems: multiple ? "center" : "unset",
            minHeight: multiple ? "18px" : "unset",
          },
          ".MuiSelect-icon": {
            fontSize: "14px",
            right: "12px !important",
            marginRight: "0px !important",
          },
          "& .MuiOutlinedInput-root": {
            "&.Mui-focused fieldset": {
              borderColor: "#D9D9D9",
              borderWidth: "1px",
            },
          },
          "& .MuiInputBase-input": {
            color: "#3e3e3e",
          },
          ...sx,
        }}
      >
        {options
          .filter((option) => !multiple || option.value !== "")
          .map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              sx={{
                fontSize: fontSize ?? { xs: "10px", sm: "12px" },
                minHeight: { xs: "25px", sm: "30px" },
                ...(multiple && {
                  "&.Mui-selected": {
                    backgroundColor: "rgba(11, 157, 189, 0.08)",
                  },
                  "&.Mui-selected:hover": {
                    backgroundColor: "rgba(11, 157, 189, 0.12)",
                  },
                }),
              }}
            >
              {option.label}
            </MenuItem>
          ))}
        {(!options || !Array.isArray(options) || options.length === 0) &&
          !placeholder && (
            <MenuItem
              value=""
              disabled
              sx={{
                fontSize: { xs: "10px", sm: "12px" },
                color: "#989898 !important",
                padding: "8px 16px",
                backgroundColor: "#EFF4FC",
                "&.Mui-disabled": {
                  color: "#989898 !important",
                  backgroundColor: "#EFF4FC",
                  opacity: 1,
                },
              }}
            >
              選択してください
            </MenuItem>
          )}
      </TextField>
    </Box>
  );
};

export default CustomSelect;
